import 'cropperjs/dist/cropper.css';
import { debounce } from 'lodash';
import { memo, useEffect, useRef, useState } from 'react';
import type { ReactCropperElement } from 'react-cropper';
import Cropper from 'react-cropper';
import styled from 'styled-components';

export interface ImageCropperProps {
  src: string | File;
  onCropComplete: (croppedImage: File) => void;
  className?: string;
  aspectRatio?: number;
}

export const ImageCropper = ({
  src,
  onCropComplete,
  className,
  aspectRatio = 1,
}: ImageCropperProps) => {
  const [image, setImage] = useState<string | null>();

  const cropperRef = useRef<ReactCropperElement>(null);

  useEffect(() => {
    if (typeof src === 'string' && !src.startsWith('data')) {
      setImage(src);
    } else if (src instanceof File) {
      void convertImageFileToBase64(src).then((base64) => {
        setImage(base64);
      });
    } else {
      setImage(null);
    }
  }, [src]);

  const onCrop = debounce(() => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.getCroppedCanvas().toBlob(
        (blob) => {
          if (blob) {
            const croppedImageFile = new File([blob], 'croppedImage.jpeg', {
              type: 'image/jpeg',
            });
            onCropComplete(croppedImageFile);
          }
        },
        'image/jpeg',
        0.8
      );
    }
  }, 500);

  if (!image) return <div></div>;

  return (
    <Container className={className}>
      {typeof image === 'string' && !image.startsWith('data') ? (
        <StaticImage src={image} alt="benefit-logo" />
      ) : (
        <StyledImage
          src={image}
          initialAspectRatio={aspectRatio}
          aspectRatio={aspectRatio}
          guides={false}
          crop={onCrop}
          ref={cropperRef}
          zoomOnWheel={false}
          zoomable={false}
        />
      )}
    </Container>
  );
};

export default memo(ImageCropper);

const convertImageFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.addEventListener('load', () => {
      resolve(reader.result as string);
    });
  });
};

const StyledImage = styled(Cropper)`
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  width: 320px;
  height: 320px;
  position: relative;
`;

const StaticImage = styled.img`
  width: 320px;
  height: 320px;
  position: absolute;
  top: 0;
  left: 0;
`;
